import { i18n } from "../translate/i18n";
import { createPurchaseValueStrategy, selectColoringStrategy } from "../viewModel/strategy/effectivePurchaseValue/index";
import { textFormatter } from "./formatter/formatter";

const { t } = i18n;
const currentLanguage = localStorage.getItem("i18nextLng") || "pt-BR";

export function getMonthName(monthNumber) {
  const date = new Date();
  date.setDate(1);
  date.setMonth(monthNumber - 1);
  const monthName = date.toLocaleString(currentLanguage, { month: "long" });
  return textFormatter(monthName);
}

export function getMonthAbbreviation(monthNumber) {
  const date = new Date();
  date.setDate(1);
  date.setMonth(monthNumber - 1);
  let monthName = date.toLocaleString(currentLanguage, { month: "short" });
  if (monthName[monthName.length - 1] === ".") monthName = monthName.slice(0, -1);
  return textFormatter(monthName);
}

export function toNDecimalsString(number, n) {
  return Number(number).toLocaleString(currentLanguage, {
    style: "decimal",
    maximumFractionDigits: n,
  });
}

export function defineGroupFilterOptions(products) {
  const level0ProductIds = products.map(product => product.productId);
  const filterOptions = [{ displayName: t("company"), value: 1, level: "level0", productIds: level0ProductIds }];

  const allLevel1Options = products.map(product => product.level1);
  const level1Options = [...new Set(allLevel1Options)];
  level1Options.forEach((option, index) => {
    const levelProductIds = products.filter(product => product.level1 === option).map(product => product.productId);
    filterOptions.push({
      displayName: textFormatter(option),
      value: index + 2,
      level: "level1",
      productIds: levelProductIds,
    });
  });

  return filterOptions;
}

export function defineMonthColumns(predictions = []) {

  const map = new Map();
  for (const p of predictions) {
    const key = `${p.year}-${p.month}`;
    if (!map.has(key)) {
      map.set(key, p);
    }
  }

  const result = Array.from(map.values()).sort(
    (a, b) =>
      a.year - b.year ||
      (a.week ?? a.month) - (b.week ?? b.month)
  );

  return result;
}

export function getProductPrediction(month, productId, predictionData) {
  const prediction = predictionData.find(
    prediction => prediction.year === month.year && prediction.month === month.month && prediction.productId === productId
  );
  return prediction !== -1 ? prediction : {};
}

export function getAllPredictionsFromMonth(month, predictionData) {
  const prediction = predictionData.filter(prediction => prediction.year === month.year && prediction.month === month.month);
  return prediction !== -1 ? prediction : {};
}

export function sumColumn(predictions, columnName) {
  return predictions.reduce((total, currentObject) => {
    if (currentObject[columnName]) return Number(total) + Number(currentObject[columnName]);
    else return total;
  }, 0);
}

function getProducts(selected, products) {
  if (selected.level === "level0") {
    return products;
  } else {
    const filteredProducts = products.filter(product =>
      product[selected.level]?.toLowerCase() === selected.groupDisplayName.toLowerCase()
    );
    return filteredProducts;
  }
}

export function getNextLevel(selected, nextLevel, products) {
  if (!selected) return

  const filteredProducts = getProducts(selected, products);
  const allNextLevelOptions = filteredProducts.map(product => product[nextLevel]);
  const nextLevelOptions = [...new Set(allNextLevelOptions)];

  return nextLevelOptions.map(option => {
    const optionProducts = filteredProducts.filter(product => product[nextLevel] === option);
    const productIds = optionProducts.map(product => product.productId);
    return { name: option, productIds: productIds };
  });
}

export function formatAdjustPlanIndex(number, unit) {
  const prefix = unit === "currency" ? "R$ " : "";
  return `${number < 0 ? "-" : ""} ${prefix} ${Math.ceil(Math.abs(number)).toLocaleString(currentLanguage)}`;
}

// TABLE CELLS
export const calculateEffectivePurchase = (value, packToUnitRatio) => {
  if (!packToUnitRatio || packToUnitRatio <= 0) return 0;
  const ratio = value / packToUnitRatio;
  const integerPart = Math.floor(ratio);
  const decimalPart = ratio - integerPart;
  if (ratio < 1 && ratio < 0.3) return 0;
  return decimalPart >= 0.3 ? integerPart + 1 : integerPart;
};

export const getSupplierId = (row, inputs, productId, suppliersAndProductRelations, isPrefixOfMeasurePage = false) => {
  if (isPrefixOfMeasurePage) return null;

  if (row.original.suppliers?.[0]?.supplierId) return row.original.suppliers[0].supplierId;
  if (row.original.supplierId) return row.original.supplierId;

  const supplierNameFromInputs = inputs?.[productId]?.suppliers;
  if (supplierNameFromInputs && suppliersAndProductRelations?.length > 0) {
    const foundSupplier = suppliersAndProductRelations.find(
      (rel) => rel.supplierName === supplierNameFromInputs
    );
    if (foundSupplier) {
      return foundSupplier.supplierId;
    }
  }

  throw new Error("Nenhum fornecedor selecionado para este produto.");
};

export const getPrefixId = (row, inputs, productId) => {

  const prefixIdFromInputs = inputs?.[productId]?.prefixId;
  if (prefixIdFromInputs) return prefixIdFromInputs;

  if (row.original?.prefixId) return row.original.prefixId;

  return 1;
};

export const getRowHighlightColors = (row, inputs, isOrderHystoryPage = false) => {
  const {
    productId,
    purchaseRec7,
    purchaseRec14,
    purchaseRec21,
    initialPurchaseRecommendation,
    purchaseStatus,
    effectivePurchase: effectivePurchaseFromDB,
    packToUnitRatio: packToUnitRatioFromDB,
    preferedSource,
  } = row.original || {};
  
  const productInputs = inputs?.[productId];
  const packToUnitRatio = productInputs?.packToUnitRatio || packToUnitRatioFromDB;
  const source = productInputs?.source;
  
  const context = {
    productInputs,
    effectivePurchaseFromDB,
    purchaseStatus,
    preferedSource,
    initialPurchaseRecommendation,
    packToUnitRatio,
    purchaseRec7,
    purchaseRec14,
    purchaseRec21,
    source,
    isOrderHystoryPage
  };
  
  //const effectivePurchase = computeEffectivePurchase(context);
  const effectivePurchase = productInputs?.effectivePurchase || createPurchaseValueStrategy({inputs, productId, row, isOrderHystoryPage});

  const coloringStrategy = selectColoringStrategy(source);

  return coloringStrategy({ ...context, effectivePurchase });
};

export const getBaseValueForEffectivePurchase = ({ data }) => {
  return createPurchaseValueStrategy(data);
};